// frontend/src/components/ProfileEditModal.tsx
import React, { useEffect, useState, useRef } from 'react';
import api from '../utils/api';
import { containsOffensive } from '../utils/safety';
import { X, LocateFixed, Check, Trash2, KeyRound, Mail } from 'lucide-react';
import MapPicker, { PlaceSelection } from './MapPicker';
import { trimGraphemes, graphemeLength } from '../utils/text';

type Props = {
  visible: boolean;
  onClose: () => void;
  user: any;
  onUpdated: (user: any) => void;
};

export default function ProfileEditModal({ visible, onClose, user, onUpdated }: Props) {
  const MAX_NAME = 15;
  const LINE_MAX = 30;
  function normalizeAbout(text: string): string {
    const lines = (text || '').replace(/\r/g, '').split('\n').slice(0, 2);
    const trimmed = lines.map((l) => trimGraphemes(l, LINE_MAX));
    return trimmed.join('\n');
  }
  const [name, setName] = useState(user?.name || '');
  const [username, setUsername] = useState((user?.username || '').toLowerCase());
  const [email, setEmail] = useState(user?.email || '');
  const initialLocSel: PlaceSelection | null = (() => {
    const loc = user?.location;
    if (!loc) return null;
    if (typeof loc === 'string') return { address: loc } as PlaceSelection;
    if (typeof loc === 'object') return loc as PlaceSelection;
    return null;
  })();
  const [locationSel, setLocationSel] = useState<PlaceSelection | null>(initialLocSel);
  const [locationText, setLocationText] = useState<string>(
    typeof user?.location === 'string' ? (user?.location as string) : (user?.location?.address || '')
  );
  const [about, setAbout] = useState(normalizeAbout(user?.about || ''));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
    const [usernameAvailable, setUsernameAvailable] = useState<null | boolean>(null);
    const [usernameChecking, setUsernameChecking] = useState<boolean>(false);
    const [usernameHint, setUsernameHint] = useState<string>('3–15 chars, lowercase, letters/numbers . _ -');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const avatarInputRef = useRef<HTMLInputElement | null>(null);
  const [locating, setLocating] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [permissionState, setPermissionState] = useState<PermissionState | null>(null);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [verifySuccess, setVerifySuccess] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const p = await checkGeoPermission();
      if (p) setPermissionState(p);
    })();
  }, []);

  useEffect(() => {
    setName(user?.name || '');
    setUsername((user?.username || '').toLowerCase());
    setEmail(user?.email || '');
    const loc = user?.location;
    if (!loc) {
      setLocationSel(null);
      setLocationText('');
    } else if (typeof loc === 'string') {
      setLocationSel({ address: loc } as PlaceSelection);
      setLocationText(loc as string);
    } else if (typeof loc === 'object') {
      setLocationSel(loc as PlaceSelection);
      setLocationText((loc as any)?.address || '');
    }
    setAbout(normalizeAbout(user?.about || ''));
    setError(null);
    setAvatarFile(null);
    setAvatarPreview(null);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError(null);
    setPasswordSuccess(null);
    setVerifyError(null);
    setVerifySuccess(null);
  }, [user, visible]);

  // Debounced username availability check
  useEffect(() => {
    const raw = (username || '').toLowerCase().replace(/[^a-z0-9_.-]/g, '');
    if (!raw) {
      setUsernameAvailable(null);
      setUsernameHint('3–15 chars, lowercase, letters/numbers . _ -');
      return;
    }
    if (raw.length < 3 || raw.length > 15) {
      setUsernameAvailable(false);
      setUsernameHint('Must be 3–15 characters');
      return;
    }
    let cancelled = false;
    setUsernameChecking(true);
    setUsernameHint('Checking availability…');
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get('/users/exists', { params: { username: raw } });
        if (cancelled) return;
        setUsernameAvailable(!!data?.available);
        setUsernameHint(data?.available ? 'Available' : 'Already taken');
      } catch (e) {
        if (cancelled) return;
        setUsernameAvailable(null);
        setUsernameHint('Could not verify, will validate on save');
      } finally {
        if (!cancelled) setUsernameChecking(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [username]);

  async function saveProfile() {
    try {
      setError(null);

      // Prepare text fields
      const safeName = trimGraphemes(name || '', MAX_NAME);
      const prevUsername = (user?.username || '').toLowerCase();
      const safeUsername = (username || '').toLowerCase().replace(/[^a-z0-9_.-]/g, '');
      let locationPayload: any = undefined;
      if (locationSel && locationSel.coordinates) {
        locationPayload = {
          address: locationSel.address || locationSel.name || locationText || '',
          city: locationSel.city || undefined,
          state: locationSel.state || undefined,
          country: locationSel.country || undefined,
          coordinates: locationSel.coordinates,
        };
      } else if (locationText) {
        locationPayload = locationText;
      }

      // Safety: limit About to two lines with per-line cap and basic profanity filter
      const safeAbout = normalizeAbout(about || '');
      if (containsOffensive(safeName) || containsOffensive(safeUsername) || containsOffensive(safeAbout)) {
        setError('Please remove offensive words from your profile fields.');
        return;
      }

      // Confirm username change
      if (safeUsername && safeUsername !== prevUsername) {
        const ok = typeof window !== 'undefined' ? window.confirm(`Change your username to @${safeUsername}? This updates your handle used for mentions and links.`) : true;
        if (!ok) return;
      }

      setSaving(true);
      const { data } = await api.put('/users/me', { name: safeName, username: safeUsername, email, location: locationPayload, about: safeAbout });
      let updatedUser = data?.user || user;

      // Optional avatar upload
      if (avatarFile) {
        const form = new FormData();
        form.append('avatar', avatarFile);
        try {
          const up = await api.post('/users/avatar', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          updatedUser = up.data?.user || updatedUser;
        } catch (e: any) {
          console.warn('Avatar upload failed:', e?.response?.data || e?.message || e);
        }
      }

      // Persist locally
      try { localStorage.setItem('user', JSON.stringify(updatedUser)); } catch {}
      onUpdated(updatedUser);
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function resendVerification() {
    try {
      setVerifyLoading(true);
      setVerifyError(null);
      setVerifySuccess(null);
      const { data } = await api.post('/auth/resend-verification');
      if (data?.success) {
        setVerifySuccess('Verification email sent. Check your inbox.');
      } else {
        setVerifyError('Could not send verification email');
      }
    } catch (e: any) {
      setVerifyError(e?.response?.data?.message || e?.message || 'Failed to resend');
    } finally {
      setVerifyLoading(false);
    }
  }

  async function changePassword() {
    try {
      setPasswordSaving(true);
      setPasswordError(null);
      setPasswordSuccess(null);
      if (newPassword.length < 8) {
        setPasswordError('New password must be at least 8 characters');
        return;
      }
      if (newPassword !== confirmPassword) {
        setPasswordError('Passwords do not match');
        return;
      }
      const { data } = await api.post('/users/change-password', { currentPassword, newPassword });
      if (data?.success) {
        setPasswordSuccess('Password updated successfully');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      }
    } catch (e: any) {
      setPasswordError(e?.response?.data?.message || e?.message || 'Failed to change password');
    } finally {
      setPasswordSaving(false);
    }
  }

  async function deleteAccount() {
    try {
      setError(null);
      const ok = deleteConfirm.trim().toUpperCase() === 'DELETE';
      if (!ok) {
        setError('Please type DELETE to confirm');
        return;
      }
      await api.delete('/users/me', { data: { currentPassword: deletePassword || undefined } });
      try { localStorage.clear(); } catch {}
      window.location.reload();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to delete account');
    }
  }

  // ---- Avatar handling: drag/drop + client-side square resize ----
  function onPickAvatar() {
    avatarInputRef.current?.click();
  }

  async function prepareAvatar(file: File) {
    try {
      const objectUrl = URL.createObjectURL(file);
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = (e) => reject(e);
        i.src = objectUrl;
      });

      const size = 512; // target square size
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('canvas_ctx');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Compute square crop from center
      const minSide = Math.min(img.naturalWidth || img.width, img.naturalHeight || img.height);
      const sx = ((img.naturalWidth || img.width) - minSide) / 2;
      const sy = ((img.naturalHeight || img.height) - minSide) / 2;

      ctx.clearRect(0, 0, size, size);
      ctx.drawImage(img, sx, sy, minSide, minSide, 0, 0, size, size);

      const blob: Blob = await new Promise((resolve) =>
        canvas.toBlob((b) => resolve(b as Blob), 'image/webp', 0.92),
      );
      const resizedFile = new File([blob], 'avatar.webp', { type: 'image/webp' });

      setAvatarFile(resizedFile);
      setAvatarPreview(URL.createObjectURL(blob));
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      // Fallback to original file if resizing fails
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  }

  function onAvatarInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Image must be under 5MB');
        return;
      }
      prepareAvatar(file);
    }
  }
  function onAvatarDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    const f = e.dataTransfer?.files?.[0];
    if (f) prepareAvatar(f);
  }

  function onAvatarDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
  }

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="themed-card rounded-2xl w-full max-w-lg shadow-2xl flex flex-col max-h-[85vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ overscrollBehavior: 'contain', touchAction: 'pan-y', borderColor: 'var(--border)' }}
      >
        <div className="flex items-center justify-between p-4 border-b sticky top-0 z-10" style={{ borderColor: 'var(--border)', background: 'var(--card)' }}>
          <h3 className="text-lg font-bold" style={{ color: 'var(--text)' }}>Edit Profile</h3>
          <button className="p-2 rounded-lg hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-blue-400/60" onClick={onClose} title="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ WebkitOverflowScrolling: 'touch' }}>
          {error && <div className="text-red-400 text-sm">{error}</div>}

          <div>
            <label className="text-xs text-theme-secondary">Display Name</label>
            <input
              className="input w-full mt-1"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={MAX_NAME}
            />
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-theme-secondary">Public name shown on your profile. Supports emoji. Different from your unique @username.</p>
              <p className="text-xs text-theme-secondary">{graphemeLength(name)}/{MAX_NAME}</p>
            </div>
          </div>

          <div>
            <label className="text-xs text-theme-secondary">Email</label>
            <input className="input w-full mt-1" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>

          {/* Username (unique handle) */}
          <div>
            <label className="text-xs text-theme-secondary">Username (handle)</label>
            <input
              className="input w-full mt-1"
              value={username}
              onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_.-]/g, ''))}
              maxLength={15}
              placeholder="yourname"
            />
            <div className="mt-1 flex items-center justify-between">
              <p className={`text-xs ${usernameAvailable === false ? 'text-red-500' : usernameAvailable ? 'text-green-600' : 'text-theme-secondary'}`}>
                {usernameChecking ? 'Checking availability…' : usernameHint}
              </p>
              <p className="text-xs text-theme-secondary">{Math.max(0, username.length)}/15</p>
            </div>
            <p className="text-[11px] text-theme-secondary mt-1">Your unique identifier: lowercase, letters/numbers . _ -. Not the same as Display Name.</p>
          </div>

          <div>
            <label className="text-xs text-theme-secondary">Location</label>
            <div className="mt-2 space-y-2">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-3 py-2 rounded-lg text-sm font-medium bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-400/60"
                  onClick={async () => {
                    try {
                      setLocating(true);
                      setGeoError(null);
                      const pos = await tryGetPrecisePosition();
                      const lat = pos.coords.latitude;
                      const lng = pos.coords.longitude;
                      const sel = await reverseGeocode(lat, lng);
                      const next = sel || { address: 'My Location', coordinates: { lat, lng } };
                      setLocationSel(next as any);
                      setLocationText(next.address || '');
                    } catch (e: any) {
                      console.warn('Geolocation failed', e);
                      // Fallback to approximate IP-based location
                      const approx = await getApproximateLocation();
                      if (approx) {
                        const sel = await reverseGeocode(approx.lat, approx.lng);
                        const next = sel || { address: 'Approximate Location', coordinates: approx };
                        setLocationSel(next as any);
                        setLocationText(next.address || '');
                        setGeoError('Using approximate location from IP (less accurate).');
                      } else {
                        const msg = deriveGeoErrorMessage(e, permissionState);
                        setGeoError(msg);
                      }
                    } finally {
                      setLocating(false);
                    }
                  }}
                  disabled={locating}
                  aria-busy={locating}
                  aria-label="Use my current location"
                >
                  <span className="inline-flex items-center gap-1">
                    <LocateFixed className="w-4 h-4" /> {locating ? 'Locating…' : 'Use My Location'}
                  </span>
                </button>
                {geoError && (
                  <span className="text-xs text-red-500 ml-2">{geoError}</span>
                )}
                {!geoError && permissionState === 'prompt' && !locating && (
                  <span className="text-xs text-theme-secondary ml-2">Tip: allow location when prompted.</span>
                )}
              </div>
              <MapPicker
                value={locationSel || (locationText ? ({ address: locationText } as PlaceSelection) : undefined)}
                onChange={(sel) => {
                  setLocationSel(sel);
                  setLocationText(sel?.address || sel?.city || sel?.name || '');
                }}
                height={240}
              />
            </div>
            <p className="text-xs text-theme-secondary mt-1">Select your city or drop a pin to set coordinates for nearby event suggestions.</p>
          </div>

          <div>
            <label className="text-xs text-theme-secondary">About</label>
            <textarea
              className="input w-full mt-1 min-h-[100px] resize-y"
              value={about}
              onChange={(e) => setAbout(normalizeAbout(e.target.value))}
              placeholder="Tell others about yourself, your interests, and experience."
            />
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-theme-secondary">Max 2 lines, 30 chars per line.</p>
              <p className="text-xs text-theme-secondary">
                {((about || '').split(/\r?\n/)[0] || '').length}/{LINE_MAX} · {((about || '').split(/\r?\n/)[1] || '').length}/{LINE_MAX}
              </p>
            </div>
          </div>

          <div className="mt-2">
            <label className="text-xs text-theme-secondary">Profile Photo</label>
            <div
              className="mt-2 rounded-xl border-2 border-dashed themed-card p-3 flex flex-col sm:flex-row items-center gap-3 hover:border-cyan-500/60 transition-colors"
              onDrop={onAvatarDrop}
              onDragOver={onAvatarDragOver}
            >
              <div className="w-24 h-24 sm:w-20 sm:h-20 rounded-xl overflow-hidden border" style={{ borderColor: 'var(--border)' }}>
                {avatarPreview ? (
                  <img src={avatarPreview} className="w-full h-full object-cover" alt="Avatar preview" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs text-theme-secondary">No image</div>
                )}
              </div>
              <div className="flex-1 w-full">
                <p className="text-xs text-theme-secondary">Drag & drop or choose a photo. Optimized to 512×512 square.</p>
                <div className="mt-2 flex flex-col sm:flex-row gap-2 w-full">
                  <button type="button" className="px-3 py-2 rounded-lg w-full sm:w-auto bg-gradient-to-r from-cyan-600 to-emerald-600 text-white shadow hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-emerald-400/60" onClick={onPickAvatar}>
                    <span className="inline-flex items-center gap-1"><Check className="w-4 h-4" /> Choose File</span>
                  </button>
                  {avatarPreview && (
                    <button type="button" className="px-3 py-2 rounded-lg w-full sm:w-auto border text-red-600 hover:bg-red-600/10 transition-colors" style={{ borderColor: 'var(--border)' }} onClick={() => { setAvatarPreview(null); setAvatarFile(null); }}>
                      <span className="inline-flex items-center gap-1"><Trash2 className="w-4 h-4" /> Remove</span>
                    </button>
                  )}
                </div>
              </div>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                onChange={onAvatarInputChange}
                className="hidden"
              />
            </div>
            <p className="text-xs text-theme-secondary mt-1">JPG/PNG/WEBP • up to 5MB • We resize and crop to a square for best fit.</p>
          </div>

          <div className="mt-6 p-4 rounded-xl border" style={{ borderColor: 'var(--border)' }}>
            <h4 className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Change Password</h4>
            {passwordError && <div className="text-red-400 text-xs mt-2">{passwordError}</div>}
            {passwordSuccess && <div className="text-green-400 text-xs mt-2">{passwordSuccess}</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <div>
                <label className="text-xs text-theme-secondary">Current Password</label>
                <input className="input w-full mt-1" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-theme-secondary">New Password</label>
                <input className="input w-full mt-1" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-theme-secondary">Confirm New Password</label>
                <input className="input w-full mt-1" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <button className="px-4 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-indigo-400/60" onClick={changePassword} disabled={passwordSaving}>
                <span className="inline-flex items-center gap-1"><KeyRound className="w-4 h-4" /> {passwordSaving ? 'Updating…' : 'Update Password'}</span>
              </button>
              <button className="px-4 py-2 rounded-lg border hover:bg-white/5 transition-colors" style={{ borderColor: 'var(--border)' }} onClick={() => { setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); setPasswordError(null); setPasswordSuccess(null); }}>Clear</button>
            </div>
            <p className="text-xs text-theme-secondary mt-2">Minimum 8 characters. If your account was created via Google, you can set a password here.</p>
          </div>

          {/* Email verification section */}
          <div className="mt-4 p-4 rounded-xl border" style={{ borderColor: 'var(--border)' }}>
            <h4 className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Verify Email</h4>
            {verifyError && <div className="text-red-400 text-xs mt-2">{verifyError}</div>}
            {verifySuccess && <div className="text-green-400 text-xs mt-2">{verifySuccess}</div>}
            <p className="text-xs text-theme-secondary mt-1">
              Status: {user?.emailVerified ? 'Verified' : 'Not verified'}
            </p>
            {!user?.emailVerified && (
              <div className="mt-3 flex gap-2">
                <button className="px-4 py-2 rounded-lg bg-gradient-to-r from-sky-600 to-cyan-600 text-white shadow hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-sky-400/60" onClick={resendVerification} disabled={verifyLoading}>
                  <span className="inline-flex items-center gap-1"><Mail className="w-4 h-4" /> {verifyLoading ? 'Sending…' : 'Resend Verification Email'}</span>
                </button>
                <span className="text-[11px] text-theme-secondary">We will send a link to {email || user?.email}.</span>
              </div>
            )}
          </div>

          {/* Danger zone: Delete account (theme-aware for readability) */}
          <div className="mt-4 p-4 rounded-xl themed-card border" style={{ borderColor: 'rgba(239,68,68,0.4)' }}>
            <h4 className="text-sm font-semibold text-red-600 dark:text-red-300">Delete Account</h4>
            <p className="text-xs text-theme-secondary mt-1">This action is permanent. Your profile will be removed and you will be signed out.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <div>
                <label className="text-xs text-theme-secondary">Type DELETE to confirm</label>
                <input className="input w-full mt-1" value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-theme-secondary">Current Password (if set)</label>
                <input className="input w-full mt-1" type="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} />
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <button className="px-4 py-2 rounded-lg bg-gradient-to-r from-red-600 to-pink-600 text-white shadow hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-red-400/60" onClick={deleteAccount}>
                <span className="inline-flex items-center gap-1"><Trash2 className="w-4 h-4" /> Delete Account</span>
              </button>
            </div>
          </div>
        </div>

        <div className="p-4 border-t flex gap-2 sticky bottom-0 z-10" style={{ borderColor: 'var(--border)', background: 'var(--card)' }}>
          <button className="flex-1 px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-600 to-cyan-600 text-white shadow hover:brightness-110 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-emerald-400/60" onClick={saveProfile} disabled={saving || usernameChecking || usernameAvailable === false}>
            <span className="inline-flex items-center gap-1"><Check className="w-4 h-4" /> {saving ? 'Saving…' : 'Save Changes'}</span>
          </button>
          <button className="flex-1 px-4 py-2 rounded-lg border hover:bg-white/5 transition-colors" style={{ borderColor: 'var(--border)' }} onClick={onClose}>
            <span className="inline-flex items-center gap-1"><X className="w-4 h-4" /> Cancel</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function getCurrentPosition(opts?: PositionOptions): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, opts);
  });
}

async function tryGetPrecisePosition(): Promise<GeolocationPosition> {
  try {
    return await getCurrentPosition({ enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 });
  } catch {}
  try {
    return await getCurrentPosition({ enableHighAccuracy: false, timeout: 8000, maximumAge: 30000 });
  } catch {}
  try {
    const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
      const id = navigator.geolocation.watchPosition(
        (p) => { navigator.geolocation.clearWatch(id); resolve(p); },
        (err) => { navigator.geolocation.clearWatch(id); reject(err); },
        { enableHighAccuracy: false, maximumAge: 0 }
      );
      setTimeout(() => { try { navigator.geolocation.clearWatch(id); } catch {} reject(new Error('watchPosition timeout')); }, 8000);
    });
    return pos;
  } catch (e) {
    throw e;
  }
}

async function reverseGeocode(lat: number, lng: number): Promise<PlaceSelection | null> {
  try {
    const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`);
    const j = await res.json();
    const address = j?.display_name || '';
    const comps = j?.address || {};
    return {
      address,
      city: comps.city || comps.town || comps.village || undefined,
      state: comps.state || undefined,
      country: comps.country || undefined,
      coordinates: { lat, lng },
    };
  } catch {
    return null;
  }
}

async function checkGeoPermission(): Promise<PermissionState | null> {
  try {
    const res = await (navigator as any).permissions?.query?.({ name: 'geolocation' as any });
    return res?.state ?? null;
  } catch {
    return null;
  }
}

function deriveGeoErrorMessage(err: any, permissionState: PermissionState | null): string {
  if (!window.isSecureContext) {
    return 'Location requires a secure context (HTTPS or localhost).';
  }
  if (permissionState === 'denied') {
    return 'Location permission denied. Enable it in your browser settings.';
  }
  if (err?.code === 1) {
    return 'Permission denied. Please allow location access.';
  }
  if (err?.code === 2) {
    return 'Position unavailable. Try again or check your connection.';
  }
  if (err?.code === 3) {
    return 'Location request timed out. Try again.';
  }
  return 'Unable to get your location.';
}

async function getApproximateLocation(): Promise<{ lat: number; lng: number } | null> {
  try {
    // Try ipapi.co
    {
      const resp = await fetch('https://ipapi.co/json/');
      if (resp.ok) {
        const data = await resp.json();
        const lat = typeof data.latitude === 'number' ? data.latitude : Number(data.latitude);
        const lng = typeof data.longitude === 'number' ? data.longitude : Number(data.longitude);
        if (isFinite(lat) && isFinite(lng)) return { lat, lng };
      }
    }
    // Try ipinfo.io
    {
      const resp = await fetch('https://ipinfo.io/json');
      if (resp.ok) {
        const data = await resp.json();
        if (typeof data.loc === 'string' && data.loc.includes(',')) {
          const [latStr, lngStr] = data.loc.split(',');
          const lat = Number(latStr);
          const lng = Number(lngStr);
          if (isFinite(lat) && isFinite(lng)) return { lat, lng };
        }
      }
    }
    // Try freegeoip.app
    {
      const resp = await fetch('https://freegeoip.app/json/');
      if (resp.ok) {
        const data = await resp.json();
        const lat = Number(data.latitude);
        const lng = Number(data.longitude);
        if (isFinite(lat) && isFinite(lng)) return { lat, lng };
      }
    }
    return null;
  } catch {
    return null;
  }
}
